import { StringTMap } from '@@types/common';
import Editor from '@@types/Editor';

import { EDITOR_ID, NEW_LINE_MANNER, PopperType } from '~/constants';
import { EDITOR_ORIGIN } from '~/constants/editor';
import { Hint } from '~/hints/Hint';
import { coachMarkStore } from '~/stores/CoachMarkStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { uiState } from '~/stores/UiState';
import domUtil from '~/utils/domUtil';
import {
  getCursor,
  getCursorAndBeforeText,
  getSelection,
  goToEnd,
  insertNewLine,
  insertText,
  refreshEditor,
  setCursor,
} from '~/utils/editorUtil';
import CodeMirror from 'codemirror';
import { debounce, isEmpty } from 'lodash';
import { observable, runInAction } from 'mobx';
import { add, evolve, isNil, keys } from 'ramda';

import Actions from '../Actions';

class EditorActions {
  public resizeObserver: ResizeObserver;
  @observable.shallow
  public heightMap: StringTMap<number> = {};

  private activeEditor: Editor = null;
  private handleResizeEditor = debounce((entries) => {
    runInAction(() => {
      entries.forEach((entry) => {
        const id = entry.target.id;
        const newHeight = entry.contentRect.height;
        if (newHeight !== this.heightMap[id]) {
          this.heightMap[id] = newHeight;
        }
      });
    });
  }, 200);

  private refreshEditor = domUtil.singletonRequestAnimationFrame(() => {
    refreshEditor(this.activeEditor);
  });

  constructor(private actions: Actions) {
    this.resizeObserver = new ResizeObserver(this.handleResizeEditor);
  }

  public setActiveEditor = (editor: Editor) => {
    this.activeEditor = editor;
  };

  public findAndSetEditorHint = ({
    editor,
    hints,
    editorId,
  }: {
    editor: Editor;
    hints: Hint[];
    editorId: EDITOR_ID;
  }) => {
    const selection = getSelection(editor);
    if (selection) {
      return;
    }
    const { cursor, text } = getCursorAndBeforeText(editor);
    this.actions.hintActions.findAndSetHint({ cursor, text, hints, editorId });
  };

  public focusEditor = (editor: Editor) => {
    if (!editor || editor.hasFocus()) {
      return;
    }
    const { main } = globalSettingStore.coachMarkComplete;
    const isModalOpened = keys(uiState.modalMap).length > 0;

    // 네이티브 앱에서 새로고침 후 바로 다른 창을 선택하더라도 에디터에 포커스 UI가 그려지기 때문에
    // 실제 document에 포커스가 있을때만 에디터에 포커스를 주도록 처리
    if (!isModalOpened && main && !coachMarkStore.isInCoachMark && this.actions.nativeActions.hasFocus()) {
      editor.focus();
    }
  };

  public handleEnterKey = (editor: Editor, key: NEW_LINE_MANNER) => {
    if (key === globalSettingStore.newLineManner) {
      insertNewLine(editor);
      return true;
    } else {
      return false;
    }
  };

  public focusAndGoToEndEditor = (editor) => {
    this.focusEditor(editor);
    goToEnd(editor);
  };

  public insertInlineCodeBlock = (editor: Editor) => {
    this.wrapSelection(
      editor,
      EDITOR_ORIGIN.INLINE_CODE_BLOCK_COMPLETE,
      (selection) => `\`${selection}\``,
      (cursor) => evolve({ ch: add(-1) }, cursor)
    );
  };

  public insertCodeBlock = (editor: Editor) => {
    this.wrapSelection(
      editor,
      EDITOR_ORIGIN.CODE_BLOCK_COMPLETE,
      (selection) => `\`\`\`\n${selection}\n\`\`\``,
      (cursor) => evolve({ line: add(-1) }, cursor)
    );
  };

  public setCursor = (editor: Editor, cursor: CodeMirror.Position) => {
    setCursor(editor, cursor);
    refreshEditor(editor);
  };

  public getEditorById = (editorId: EDITOR_ID) => {
    switch (editorId) {
      case EDITOR_ID.MAIN:
        return mainDetailUiState.mainEditor;
      case EDITOR_ID.LOG:
        return mainDetailUiState.logEditor;
      case EDITOR_ID.THREAD_MAIN:
        return threadDetailUiState.mainEditor;
      case EDITOR_ID.THREAD_LOG:
        return threadDetailUiState.logEditor;
    }
  };

  public openEditorPopper = (editorId: string, wrapper: HTMLElement) => {
    uiState.openPopper({
      name: PopperType.EditorPopper,
      args: {
        referenceNode: wrapper,
        options: { placement: 'top' },
        editorId,
      },
      place: editorId === EDITOR_ID.MAIN || editorId === EDITOR_ID.LOG ? 'main' : 'panel',
    });
  };

  public insertTextToActiveEditor = (text: string) => {
    const editor = this.activeEditor;
    if (isNil(editor)) return;
    insertText(editor, text, EDITOR_ORIGIN.PASTE_COMPLETE);
    this.focusEditor(editor);
    // 특정 글자 이상 붙여 넣기 할 경우 커서 위치가 잘못 잡히는 현상 수정
    // https://github.com/codemirror/CodeMirror/issues/1642
    this.refreshEditor();
  };

  private wrapSelection = (
    editor: Editor,
    origin: EDITOR_ORIGIN,
    wrap: (selection: string) => string,
    getNewPos: (cursor: CodeMirror.Position) => CodeMirror.Position
  ) => {
    const selection = getSelection(editor);
    let cursor = getCursor(editor);
    insertText(editor, wrap(selection), origin);
    if (isEmpty(selection)) {
      cursor = getCursor(editor);
      this.setCursor(editor, getNewPos(cursor));
    }
  };
}

export default EditorActions;
